import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Box, IconButton, Grid, Typography } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';

import { isMobileApp } from '../../utils/site';
import './MainHeader.css';
import { useThemeContext } from '../../contexts/ThemeContext.js';
import { AUTHENTICATED, UNAUTHENTICATED } from '../../constants/user';
import useNotificationCount from '../../hooks/useNotificationCount';
import BorderedButton from '../common/buttons/BorderedButton';
import PrimaryBlueButton from '../common/buttons/PrimaryBlueButton';
import DrawerMobileMenu from './DrawerMobileMenu';
import { fonts } from '../../styles/fontStyles.js';
import AvatarInitials from '../user/AvatarInitials';
import MainLogo from './MainLogo';
import OrgAccountDrawer from '../header/OrgAccountDrawer';
import { getSaasLinkSx } from '../../styles/saasLinkStyles';
import AccountNameDescription from '../../routes/organizations/accounts/account/AccountNameDescription';

const app_co = process.env.REACT_APP_COMPANY;

const MainHeader = ({ activeLink, showLoginOption, headerClassName = 'container' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userType, cognitoUserInfo, orgs, accounts, theme, isUserDataReady } = useThemeContext();
  const [errorMessage, setErrorMessage] = useState('');
  const { notificationCount } = useNotificationCount(isUserDataReady, userType, setErrorMessage);
  const isMobile = useMediaQuery(theme.breakpoints.down('smMd700'));
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const { org_path_name, acc_path_name } = useParams();

  const hasCurrentOrganizationAccess = useMemo(
    () => Array.isArray(orgs) && orgs.some((organization) => organization?.path_name === org_path_name),
    [org_path_name, orgs],
  );

  const hasCurrentAccountAccess = useMemo(
    () =>
      Array.isArray(accounts) &&
      accounts.some((account) => {
        const accountOrgPath = account?.__orgPath || account?.org_path_name;
        return accountOrgPath === org_path_name && (!acc_path_name || account?.path_name === acc_path_name);
      }),
    [acc_path_name, accounts, org_path_name],
  );

  const isAccountOnlyView = hasCurrentAccountAccess && !hasCurrentOrganizationAccess;

  useEffect(() => {
    if (cognitoUserInfo && cognitoUserInfo.attributes) {
      setFullName(`${cognitoUserInfo.attributes.name} ${cognitoUserInfo?.attributes?.family_name}`);
      setEmail(cognitoUserInfo?.attributes?.email);
    }
  }, [cognitoUserInfo]);

  // users even with just one account can see the org/acc menus
  const accountIfSingle = useMemo(() => {
    if (accounts && accounts.length === 1) {
      return accounts[0];
    }
    return null;
  }, [accounts]);

  const getHeaderLinkStylesFor = (linkName) => {
    const active = activeLink === linkName;
    return {
      color: active ? theme.typography.links.desktopMenu.activeColor : theme.typography.links.desktopMenu.color,
      verticalAlign: 'middle',
      textAlign: 'center',
      textDecoration: 'none',
      fontFamily: fonts.titlesNavButtons,
      fontSize: '16px',
      fontStyle: 'normal',
      fontWeight: '500',
      lineHeight: '5px',
      letterSpacing: '0.5px',
    };
  };

  const renderStroke = (linkName) => {
    return activeLink === linkName ? (
      <svg xmlns="http://www.w3.org/2000/svg" width="100" height="3" viewBox="0 0 100 3" fill="none">
        <path
          d="M2 1.5H100"
          stroke={theme.typography.links.desktopMenu.underlineNavigationActive}
          strokeWidth="3"
          strokeLinecap="round"
          style={{ strokeWidth: '3px', stroke: theme.typography.links.desktopMenu.underlineNavigationActive }}
        />
      </svg>
    ) : null;
  };

  const buildBreadcrumbItems = () => {
    const availableOrgs = Array.isArray(orgs) ? orgs : [];
    const availableAccounts = Array.isArray(accounts) ? accounts : [];
    if ((!availableOrgs.length && !availableAccounts.length) || userType !== AUTHENTICATED) {
      return [];
    }

    const items = [];
    const encodedOrg = encodeURIComponent(org_path_name || '');

    if (hasCurrentOrganizationAccess) {
      // If the user has more than 1 organization, show the parent 'Organizations' root link for easy switching
      if (org_path_name) {
        items.push({ label: org_path_name, href: `/o/${encodedOrg}` });
      }
    }

    const pathSegments = location.pathname.split('/').filter(Boolean);

    const isAccountsRoot =
      pathSegments.length === 3 && pathSegments[0] === 'o' && pathSegments[2] === 'a' && !acc_path_name;

    if ((isAccountsRoot || acc_path_name) && (availableAccounts.length > 1 || isAccountOnlyView)) {
      items.push({ label: 'Accounts', href: `/o/${encodedOrg}/a` });
    }

    let encodedAcc;
    if (acc_path_name) {
      encodedAcc = encodeURIComponent(acc_path_name);
      items.push({
        label: acc_path_name,
        href: `/o/${encodedOrg}/a/${encodedAcc}`,
      });
    }

    const lastSegment = pathSegments[pathSegments.length - 1];
    const isStaffPage = lastSegment === 'staff';

    if (isStaffPage) {
      const staffHref = acc_path_name ? `/o/${encodedOrg}/a/${encodedAcc}/staff` : `/o/${encodedOrg}/staff`;
      items.push({ label: 'Staff', href: staffHref });
    }

    const locationsIndex = pathSegments.findIndex((segment) => segment === 'locations');
    if (locationsIndex !== -1) {
      const locationsHref = acc_path_name ? `/o/${encodedOrg}/a/${encodedAcc}/locations` : `/o/${encodedOrg}/locations`;
      items.push({ label: 'Locations', href: locationsHref });

      const locationSegment = pathSegments[locationsIndex + 1];
      if (locationSegment) {
        items.push({
          label: decodeURIComponent(locationSegment),
          href: `${locationsHref}/${locationSegment}`,
        });
      }
    }

    const eventsIndex = pathSegments.findIndex((segment) => segment === 'events');
    if (eventsIndex !== -1) {
      const eventsHref = acc_path_name ? `/o/${encodedOrg}/a/${encodedAcc}/events` : `/o/${encodedOrg}/events`;
      items.push({ label: 'Events', href: eventsHref });

      const eventSegment = pathSegments[eventsIndex + 1];
      if (eventSegment) {
        items.push({
          label: decodeURIComponent(eventSegment),
          href: `${eventsHref}/${eventSegment}`,
        });
      }
    }

    const ordersIndex = pathSegments.findIndex((segment) => segment === 'orders');
    if (ordersIndex !== -1) {
      const ordersHref = `/o/${encodedOrg}/orders`;
      items.push({ label: 'Orders', href: ordersHref });

      const orderSegment = pathSegments[ordersIndex + 1];
      if (orderSegment) {
        items.push({
          label: decodeURIComponent(orderSegment),
          href: `${ordersHref}/${orderSegment}`,
        });
      }
    }

    return items;
  };

  const breadcrumbItems = useMemo(buildBreadcrumbItems, [
    orgs,
    userType,
    acc_path_name,
    accounts,
    hasCurrentOrganizationAccess,
    isAccountOnlyView,
    location.pathname,
    org_path_name,
  ]);

  if (isMobileApp) return;

  const renderMobileOrDesktopMenu = () => {
    if (isMobile) {
      return <DrawerMobileMenu fullName={fullName} userType={userType} email={email} />;
    } else if (app_co === 'frost') {
      return renderSaasMenu();
    } else {
      return renderDesktopMenu();
    }
  };

  const strokeBoxStyles = {
    width: '100%',
    paddingLeft: '0px',
  };

  const renderNotificationBadge = (notificationCount) => {
    if (notificationCount !== null && notificationCount > 0) {
      return (
        <Box
          sx={{
            position: 'absolute',
            padding: '0px 4px',
            top: 0,
            right: 0,
            transform: 'translate(35%, -35%)',
            backgroundColor: theme.custom.pages.auth.home.desktopMenu.notificationCount.badge.backgroundColor,
            color: theme.custom.pages.auth.home.desktopMenu.notificationCount.badge.color,
            width: 20,
            height: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '11px',
            fontWeight: 700,
            borderRadius: '100px',
            border: `2px solid ${theme.custom.pages.auth.home.desktopMenu.notificationCount.badge.borderColor}`,
          }}
        >
          {notificationCount < 10 ? notificationCount : '9+'}
        </Box>
      );
    }
    return null;
  };

  const renderDesktopMenu = () => {
    return (
      <Grid
        container
        sx={{
          maxWidth: '65%',
          justifyContent: 'space-between',
          padding: '10px 5px 10px 0px',
        }}
      >
        <Grid item xs={2}>
          <Link className="header-report-link" to="/results.html">
            <Box className="icon-wrapper">
              <img
                src={
                  activeLink === 'Results'
                    ? theme.images.desktopMenu.results.active
                    : theme.images.desktopMenu.results.default
                }
                alt="results icon"
              />
              <Typography className="header-text" sx={getHeaderLinkStylesFor('Results')}>
                Results
              </Typography>
            </Box>
            <Box sx={{ ...strokeBoxStyles, maxHeight: '5px' }}>{renderStroke('Results')}</Box>
          </Link>
        </Grid>
        <Grid item xs={2}>
          <Link className="header-report-link" to="/orders.html">
            <Box className="icon-wrapper">
              <img
                src={
                  activeLink === 'Orders'
                    ? theme.images.desktopMenu.orders.active
                    : theme.images.desktopMenu.orders.default
                }
                alt="order icon"
              />
              <Typography className="header-text" sx={getHeaderLinkStylesFor('Orders')}>
                Orders
              </Typography>
            </Box>
            <Box sx={strokeBoxStyles}>{renderStroke('Orders')}</Box>
          </Link>
        </Grid>
        <Grid item xs={2}>
          <Link className="header-report-link" to="/support.html">
            <Box className="icon-wrapper">
              <img
                src={
                  activeLink === 'Support'
                    ? theme.images.desktopMenu.support.active
                    : theme.images.desktopMenu.support.default
                }
                alt="support icon"
              />
              <Typography className="header-text" sx={getHeaderLinkStylesFor('Support')}>
                Support
              </Typography>
            </Box>
            <Box sx={strokeBoxStyles}>{renderStroke('Support')}</Box>
          </Link>
        </Grid>
        <Grid item xs={2}>
          <Link className="header-report-link" to="/profile.html">
            <Box className="icon-wrapper" sx={{ position: 'relative', display: 'inline-flex' }}>
              <IconButton color="inherit" aria-label="open drawer" edge="start">
                <AvatarInitials fullName={fullName} />
              </IconButton>
              {renderNotificationBadge(notificationCount)}
            </Box>
          </Link>
        </Grid>
      </Grid>
    );
  };

  const renderBreadcrumbs = ({ saasLinkStyles, activeCrumbStyles }) => {
    if (!breadcrumbItems.length) {
      return null;
    }

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'nowrap',
          whiteSpace: 'nowrap',
          overflowX: 'auto',
          overflowY: 'hidden',
          minWidth: 0,
          pr: 1,
        }}
      >
        {breadcrumbItems.map((item, index) => {
          const isLast = index === breadcrumbItems.length - 1;
          const typography = (
            <Typography
              component="span"
              noWrap
              sx={{
                ...(isLast ? activeCrumbStyles : saasLinkStyles),
                display: 'inline-flex',
                alignItems: 'center',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '100%',
              }}
            >
              {item.label}
            </Typography>
          );

          return (
            <React.Fragment key={`${item.label}-${index}`}>
              {index > 0 && (
                <Typography component="span" sx={{ display: 'inline-flex' }}>
                  {'>'}
                </Typography>
              )}
              {isLast ? (
                typography
              ) : (
                <Link className="" to={item.href} style={{ textDecoration: 'none' }}>
                  {typography}
                </Link>
              )}
            </React.Fragment>
          );
        })}
      </Box>
    );
  };

  const renderSaasMenu = () => {
    const saasLinkStyles = getSaasLinkSx(theme);
    const activeCrumbStyles = {
      ...saasLinkStyles,
      color: theme?.typography?.links?.desktopMenu?.activeColor ?? saasLinkStyles.color,
      textDecoration: 'none',
      cursor: 'default',
      '&:hover': {
        color: theme?.typography?.links?.desktopMenu?.activeColor ?? saasLinkStyles.color,
        textDecoration: 'none',
      },
    };

    const isOrgSection = location.pathname.split('/').filter(Boolean)[0] === 'o';

    return (
      <Grid item xs={10}>
        <Grid
          container
          sx={{
            justifyContent: 'space-between',
            padding: '10px 5px 10px 0px',
            height: '100%',
            alignItems: 'center',
          }}
        >
          <Grid
            item
            xs
            sx={{
              flexGrow: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            {app_co === 'frost' &&
              userType === AUTHENTICATED &&
              accountIfSingle &&
              isOrgSection &&
              !isAccountOnlyView && (
                <Box sx={{ mb: 0.5 }}>
                  <AccountNameDescription account={accountIfSingle} variant="header" />
                </Box>
              )}

            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pl: '12px' }}>
              {isOrgSection ? (
                breadcrumbItems.length > 0 ? (
                  renderBreadcrumbs({ saasLinkStyles, activeCrumbStyles })
                ) : null
              ) : (
                <Box sx={{ display: 'flex', width: '100%', gap: 4 }}>
                  <Link className="header-report-link" to="/appointments.html">
                    <Box className="icon-wrapper" sx={{ width: 'auto !important' }}>
                      <img
                        src={
                          activeLink === 'Appointments'
                            ? theme.images.desktopMenu.calendar.active
                            : theme.images.desktopMenu.calendar.default
                        }
                        alt="order icon"
                      />
                      <Typography className="header-text" sx={getHeaderLinkStylesFor('Appointments')}>
                        Appointments
                      </Typography>
                    </Box>
                    <Box sx={strokeBoxStyles}>{renderStroke('Appointments')}</Box>
                  </Link>
                  <Link className="header-report-link" to="/records.html">
                    <Box className="icon-wrapper" sx={{ width: 'auto !important' }}>
                      <img
                        src={
                          activeLink === 'Records'
                            ? theme.images.desktopMenu.records.active
                            : theme.images.desktopMenu.records.default
                        }
                        alt="records icon"
                      />
                      <Typography className="header-text" sx={getHeaderLinkStylesFor('Records')}>
                        Records
                      </Typography>
                    </Box>
                    <Box sx={strokeBoxStyles}>{renderStroke('Records')}</Box>
                  </Link>
                  <Link className="header-report-link" to="/inbox.html">
                    <Box className="icon-wrapper" sx={{ width: 'auto !important' }}>
                      <img
                        src={
                          activeLink === 'Messages'
                            ? theme.images.desktopMenu.messages.active
                            : theme.images.desktopMenu.messages.default
                        }
                        alt="messages icon"
                      />
                      <Typography className="header-text" sx={getHeaderLinkStylesFor('Messages')}>
                        Messages
                      </Typography>
                    </Box>
                    <Box sx={strokeBoxStyles}>{renderStroke('Messages')}</Box>
                  </Link>
                </Box>
              )}
            </Box>
          </Grid>

          <Grid item xs={2}>
            <Link className="header-report-link" to="/profile.html">
              <Box className="icon-wrapper" sx={{ position: 'relative', display: 'inline-flex' }}>
                <IconButton color="inherit" aria-label="open drawer" edge="start">
                  <AvatarInitials fullName={fullName} />
                </IconButton>
                {renderNotificationBadge(notificationCount)}
              </Box>
            </Link>
          </Grid>
        </Grid>
      </Grid>
    );
  };

  const renderMenuElements = () => {
    if (userType === UNAUTHENTICATED && !showLoginOption) return;

    if (userType === UNAUTHENTICATED && !isMobile)
      if (app_co === 'frost') {
        return (
          <Grid item xs={10}>
            <Grid
              item
              container
              spacing={{ xs: 2, md: 2 }}
              columns={{ xs: 4, sm: 8, md: 12 }}
              justifyContent="flex-begin"
            >
              <Grid item xs={2} sm={6} md={8} sx={{ paddingLeft: '10px', display: { xs: 'none', sm: 'inherit' } }}>
                <Typography sx={{ paddingTop: '10px' }}>Fast Response On-Site Testing (FROST)</Typography>
              </Grid>
              <Grid item xs={2} sm={2} md={2} sx={{ textAlign: 'left', marginRight: { xs: '30px', sm: '0px' } }}>
                <PrimaryBlueButton
                  onClick={() => navigate('/login')}
                  buttonText="Sign In"
                  sxProps={{
                    height: { md: 45, xs: 46, sm: 46 },
                    width: { md: 158, xs: 90, sm: 100 },
                    fontSize: '0.875rem',
                    padding: { md: '8px 24px', xs: '2px 6px' },
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
        );
      } else {
        return (
          <Grid item xs={8}>
            <Grid
              item
              container
              spacing={{ xs: 2, md: 2 }}
              columns={{ xs: 4, sm: 8, md: 12 }}
              justifyContent="flex-end"
            >
              <Grid item xs={2} sm={3} md={4} sx={{ textAlign: 'right', marginRight: { xs: '30px', sm: '0px' } }}>
                <PrimaryBlueButton
                  onClick={() => navigate('/login')}
                  buttonText="Sign In"
                  sxProps={{
                    height: { md: 45, xs: 46, sm: 46 },
                    width: { md: 158, xs: 90, sm: 100 },
                    fontSize: '0.875rem',
                    padding: { md: '8px 24px', xs: '2px 6px' },
                  }}
                />
              </Grid>
              <Grid item xs={2} sm={3} md={4} sx={{ paddingLeft: '10px', display: { xs: 'none', sm: 'inherit' } }}>
                <BorderedButton
                  onClick={() => navigate('/activate.html')}
                  buttonText="Activate test"
                  sxProps={{
                    height: { md: 45, xs: 46, sm: 46 },
                    width: { md: 158, xs: 110, sm: 135 },
                    fontSize: { md: '0.875rem', xs: '0.7rem', sm: '0.875rem' },
                    padding: { md: '8px 10px', xs: '2px 6px' },
                    '& .MuiButton-startIcon': {
                      height: '18px',
                      width: '18px',
                      backgroundImage: `url(${theme.images.activateKitIcon.default})`,
                    },
                    '&:hover': {
                      background: 'transparent',
                      color: theme.buttons.ghost.hover.color,
                      border: '2px solid',
                      '& .MuiButton-startIcon': {
                        height: '18px',
                        width: '18px',
                        backgroundImage: `url(${theme.images.activateKitIcon.hover})`,
                      },
                    },
                  }}
                  additionalProps={{
                    // Needed so the icon styles work as expected
                    startIcon: <></>,
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
        );
      }

    return renderMobileOrDesktopMenu();
  };

  return (
    <Grid
      key="grid-header"
      container
      spacing={1}
      id="header-container"
      className={headerClassName}
      maxWidth="md"
      sx={{
        marginBottom: { md: '24px', sm: '0px' },
        paddingLeft: { md: 'none', xs: '16px' },
        paddingRight: { md: 'none', xs: '16px' },
      }}
    >
      <Grid
        item
        xs={2}
        sx={{
          paddingLeft: '0px',
          display: 'flex',
          alignItems: 'initial',
          '&.MuiGrid-item': {
            paddingTop: { md: '0px', sm: '8px' },
          },
        }}
      >
        {app_co === 'frost' && userType === AUTHENTICATED && <OrgAccountDrawer />}
        <MainLogo />
      </Grid>
      {renderMenuElements()}
      {errorMessage && <h4>{errorMessage}</h4>}
    </Grid>
  );
};

export default MainHeader;
